/**
 * ref-cycle lint rule — finds reference cycles in the entity graph.
 *
 * Cycles aren't always wrong (e.g. linked lists), but they should be a
 * deliberate decision — surface them so reviewers can confirm.
 */
import type { AtlasManifest } from '../types.js';

export interface CycleFinding {
  ref: string;
  message: string;
}

export function refCycle(manifest: AtlasManifest): CycleFinding[] {
  // Build adjacency list (directed graph: from → to).
  const out = new Map<string, string[]>();
  for (const r of manifest.references) {
    if (r.from === r.to) continue; // self-loops are filtered separately
    const list = out.get(r.from) ?? [];
    list.push(r.to);
    out.set(r.from, list);
  }

  // Iterative DFS detecting back-edges; collects the cycle path.
  const findings: CycleFinding[] = [];
  const cyclesSeen = new Set<string>();
  const WHITE = 0;
  const GRAY = 1;
  const BLACK = 2;
  const color = new Map<string, number>();

  for (const node of out.keys()) {
    if (color.get(node) !== undefined) continue;
    // DFS with explicit stack of (node, neighbours iterator, parent path)
    const stack: Array<{ node: string; iter: Iterator<string>; path: string[] }> = [
      { node, iter: (out.get(node) ?? [])[Symbol.iterator](), path: [node] },
    ];
    color.set(node, GRAY);

    while (stack.length > 0) {
      const top = stack[stack.length - 1]!;
      const step = top.iter.next();
      if (step.done) {
        color.set(top.node, BLACK);
        stack.pop();
        continue;
      }
      const next = step.value;
      const c = color.get(next) ?? WHITE;
      if (c === GRAY) {
        // Back-edge → cycle. Slice path from `next` to `top.node`.
        const idx = top.path.indexOf(next);
        if (idx >= 0) {
          const cycle = [...top.path.slice(idx), next];
          const key = canonicalCycleKey(cycle);
          if (!cyclesSeen.has(key)) {
            cyclesSeen.add(key);
            findings.push({
              ref: cycle.join(' → '),
              message: `Reference cycle (${cycle.length - 1} hops): ${cycle.join(' → ')}`,
            });
          }
        }
      } else if (c === WHITE) {
        color.set(next, GRAY);
        stack.push({
          node: next,
          iter: (out.get(next) ?? [])[Symbol.iterator](),
          path: [...top.path, next],
        });
      }
    }
  }
  return findings;
}

/** Rotate cycle so its lexicographically smallest node is first → stable key. */
function canonicalCycleKey(cycle: string[]): string {
  // cycle is like ['A','B','C','A'] — drop the duplicate tail
  const nodes = cycle.slice(0, -1);
  let minIdx = 0;
  for (let i = 1; i < nodes.length; i++) {
    if (nodes[i]! < nodes[minIdx]!) minIdx = i;
  }
  return [...nodes.slice(minIdx), ...nodes.slice(0, minIdx)].join('|');
}
